/**
 * Minimal hash-based router for the storefront Mini App (zero external deps).
 *
 * Telegram WebApps run in an embedded webview where the History API behaves
 * inconsistently across clients; a hash strategy (``#/shop/3``) is reliable. The
 * router exposes the current {@link RouteMatch} and an imperative {@link Navigate}
 * helper, both via context so any layer can read/route without prop drilling.
 *
 * It deliberately mirrors only what the app needs (push/replace/back) — it is not a
 * general-purpose router.
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type MouseEvent,
  type ReactNode,
} from "react";

import { stripTelegramHashParams } from "./deepLink";
import { matchRoute, type RouteMatch } from "./routes";

export interface Navigate {
  /** Push a new entry (adds to history). */
  (path: string): void;
  /** Replace the current entry (no new history). */
  readonly replace: (path: string) => void;
  /** Go back one entry. */
  readonly back: () => void;
}

interface RouterContextValue {
  readonly match: RouteMatch;
  readonly path: string;
  readonly navigate: Navigate;
}

const RouterContext = createContext<RouterContextValue | null>(null);

/** Read the current path from ``location.hash`` (``#/foo`` → ``/foo``).
 *
 * Telegram launches the Mini App with its own params appended to the hash
 * (e.g. ``#tgWebAppData=...&tgWebAppVersion=...``). Those are NOT app routes, so we
 * only treat the hash as a route when it is an explicit ``/...`` path; anything else
 * (Telegram params, empty) resolves to home. This prevents a spurious 404 on launch.
 */
export function readPathFromHash(): string {
  const hash = window.location.hash.replace(/^#/, "");
  if (!hash.startsWith("/")) {
    return "/";
  }
  // Telegram may append its own params to the hash (``#/orders/1&tgWebAppData=…``);
  // keep only the leading ``/path`` so the route still resolves.
  const cleaned = stripTelegramHashParams(hash);
  return cleaned.startsWith("/") ? cleaned : "/";
}

export interface RouterProviderProps {
  readonly children: ReactNode;
}

export function RouterProvider({ children }: RouterProviderProps): JSX.Element {
  const [path, setPath] = useState<string>(() =>
    typeof window === "undefined" ? "/" : readPathFromHash(),
  );

  useEffect(() => {
    const onHashChange = (): void => {
      setPath(readPathFromHash());
    };
    window.addEventListener("hashchange", onHashChange);
    // Normalize an empty hash to the home route on first mount.
    if (window.location.hash === "") {
      window.location.replace(`#${path}`);
    }
    return () => {
      window.removeEventListener("hashchange", onHashChange);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const navigate = useMemo<Navigate>(() => {
    const push = (next: string): void => {
      window.location.hash = next;
    };
    const replace = (next: string): void => {
      const url = `${window.location.pathname}${window.location.search}#${next}`;
      window.history.replaceState(null, "", url);
      setPath(readPathFromHash());
    };
    const back = (): void => {
      window.history.back();
    };
    return Object.assign(push, { replace, back });
  }, []);

  const value = useMemo<RouterContextValue>(
    () => ({ match: matchRoute(path), path, navigate }),
    [path, navigate],
  );

  return (
    <RouterContext.Provider value={value}>{children}</RouterContext.Provider>
  );
}

function useRouterContext(): RouterContextValue {
  const ctx = useContext(RouterContext);
  if (ctx === null) {
    throw new Error("useRouter must be used within <RouterProvider>");
  }
  return ctx;
}

/** Current typed route match. */
export function useRoute(): RouteMatch {
  return useRouterContext().match;
}

/** Imperative navigation helper (``navigate(path)``, ``.replace``, ``.back``). */
export function useNavigate(): Navigate {
  return useRouterContext().navigate;
}

export interface LinkProps {
  readonly to: string;
  readonly children: ReactNode;
  readonly className?: string;
  readonly replace?: boolean;
}

/** Accessible in-app link that routes via the hash router. */
export function Link({
  to,
  children,
  className,
  replace = false,
}: LinkProps): JSX.Element {
  const navigate = useNavigate();
  const onClick = useCallback(
    (event: MouseEvent<HTMLAnchorElement>): void => {
      event.preventDefault();
      if (replace) {
        navigate.replace(to);
      } else {
        navigate(to);
      }
    },
    [navigate, to, replace],
  );
  return (
    <a href={`#${to}`} className={className} onClick={onClick}>
      {children}
    </a>
  );
}
